import { FC, useCallback, useMemo, useState } from "react";
import { Trans } from "@lingui/macro";
import { BrandSaleType, ShopType, WishlistItemType } from "src/types/graphql.d";
import { Flex } from "@otrium/core";
import { Tag } from "src/icons/Tag";
import { FollowBrandButton } from "src/molecules/FollowBrandButton";
import { BrandCover } from "src/molecules/BrandCover";
import { useAddToWishList } from "src/hooks/useAddToWishlist";
import { useDeleteWishlistItem } from "src/hooks/deleteWishlistItem";
import { useToggleWishlistBrandItem } from "src/hooks/toggleWishlistItem";
import { useTheme, Theme } from "@emotion/react";
import { useSegment } from "src/hooks/useSegment";
import { BrandInfoModal } from "src/molecules/BrandInfoModal";
import { StyledText } from "./BrandViewBrandCover.styled";
import type { BrandData } from "src/types/adapters/browse.type";

interface Props {
  brand: BrandData;
  genderId: ShopType;
  isLoggedIn: boolean;
  isComingSoon?: boolean;
  noPadding?: boolean;
  fullWidth?: boolean;
  legacy?: boolean;
}

const BrandViewBrandCover: FC<Props> = ({
  brand,
  genderId,
  isLoggedIn,
  isComingSoon = false,
  noPadding,
  fullWidth,
  legacy,
}) => {
  const [isOpenBrandInfo, setIsOpenBrandInfo] = useState(false);

  const { deleteWishListItem, loading: deleteLoading } = useDeleteWishlistItem({
    refetchQueries: ["userWishlistMetaData"],
  });

  const { addToWishlist, loading: addLoading } = useAddToWishList({
    refetchQueries: ["userWishlistMetaData"],
  });

  const { addedToWishlist } = useToggleWishlistBrandItem(brand.id);

  const theme: Theme = useTheme();

  const { brandFavourited, brandUnfavourited, segmentBrandInfoOpened } =
    useSegment();

  const brandWishlistTracking = useMemo(
    () => ({
      brandName: brand.name,
      brandId: brand.id,
    }),
    [brand.name, brand.id]
  );

  const onAddToWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await addToWishlist(itemType, itemId);
      void brandFavourited(brandWishlistTracking);
    },
    [addToWishlist, brandFavourited, brandWishlistTracking]
  );

  const onRemoveFromWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await deleteWishListItem(itemType, itemId, {});
      void brandUnfavourited(brandWishlistTracking);
    },
    [deleteWishListItem, brandUnfavourited, brandWishlistTracking]
  );

  const onToggleFollow = useCallback(
    async (id: string, type: WishlistItemType) => {
      if (addedToWishlist) {
        await onRemoveFromWishList(type, id);
      } else {
        await onAddToWishList(type, id);
      }
    },
    [addedToWishlist, onAddToWishList, onRemoveFromWishList]
  );

  const handleCloseBrandInfo = useCallback(() => {
    setIsOpenBrandInfo(false);
  }, []);

  const onShowBrandInfo = useCallback(() => {
    void segmentBrandInfoOpened({
      pageType: "Brand Page",
      brandName: brand.name,
    });
    setIsOpenBrandInfo(true);
  }, [segmentBrandInfoOpened, brand.name]);

  const brandCoverActions =
    brand.brand_sale_type !== BrandSaleType.FlashSale ? (
      <>
        {isLoggedIn && brand.id && (
          <Flex
            sx={{ paddingLeft: "30px", border: "none" }}
            justifyContent="center"
            alignItems="center"
          >
            <FollowBrandButton
              following={addedToWishlist}
              onClick={() =>
                void onToggleFollow(brand.id, WishlistItemType.Brand)
              }
              loading={deleteLoading || addLoading}
            />
          </Flex>
        )}
        <Flex
          onClick={onShowBrandInfo}
          as="button"
          sx={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            position: "relative",
            "& > svg": {
              path: {
                transition: "all 0.2s ease-in-out",
                stroke: `${theme.colors.tone.white}`,
                fill: "transparent",
              },
            },
            "&:hover > svg": {
              path: {
                fill: "rgba(255,255,255, 0.5)",
              },
            },
          }}
          ml={5}
          pr={3}
          justifyContent="center"
          alignItems="center"
        >
          <Tag />
          <StyledText ml={2}>
            <Trans>Info</Trans>
          </StyledText>
        </Flex>
      </>
    ) : null;

  return (
    <>
      <BrandInfoModal
        open={isOpenBrandInfo}
        onClose={handleCloseBrandInfo}
        brand={brand}
      />
      <BrandCover
        brand={brand}
        genderId={genderId}
        isComingSoon={isComingSoon}
        actions={brandCoverActions}
        disablePadding={noPadding}
        fullWidth={fullWidth}
        legacy={legacy}
      />
    </>
  );
};

export default BrandViewBrandCover;
